/**
 * Action-Level ACL
 *
 * Provides action-based permission checks using data from action-allowed-roles.json.
 * This JSON is generated from an Excel Action-Role Matrix.
 *
 * Data Flow:
 *   Excel Action-Role Matrix → JSON → Guard component → UI visibility
 */

import { Role } from './roles'
import actionAllowedRolesJson from './action-allowed-roles.json'
import { Auth } from '@/api/auth'

/** Reverse map: Role value → Role enum key (e.g. 'SLD:OFFICER' → 'SALES_OFFICER') */
const roleValueToKey = Object.fromEntries(
  (Object.entries(Role) as [string, string][]).map(([key, value]) => [
    value,
    key,
  ])
)

interface ActionEntry {
  description: string
  allowedRoles: string[]
}

interface MenuActions {
  path: string
  actions: Record<string, ActionEntry>
}

type ActionAllowedRolesData = Record<string, MenuActions>

const actionData = actionAllowedRolesJson as unknown as ActionAllowedRolesData

/**
 * Get the allowed roles for a specific menu action.
 * Returns the list of Role enum keys that can perform this action.
 * Returns undefined if the menu or action is not found.
 *
 * Note: SUPER and ADMIN are handled implicitly at the Guard level
 * and should NOT be included in these lists.
 */
export function getActionAllowedRoles(
  menu: string,
  action: string
): string[] | undefined {
  const menuEntry = actionData[menu]
  if (!menuEntry) return undefined

  const actionEntry = menuEntry.actions[action]
  if (!actionEntry) return undefined

  return actionEntry.allowedRoles
}

/**
 * Check if a given set of user roles can perform a specific action.
 * Returns null if the action definition is not found (caller should fallback).
 * SUPERADMIN always has access.
 */
export function canPerformMenuAction(
  menu: string,
  action: string,
  userRoles: string[]
): boolean | null {
  if (userRoles.includes('SUPERADMIN')) return true

  const allowedRoles = getActionAllowedRoles(menu, action)
  if (!allowedRoles) return null

  // userRoles are Role values (e.g. 'SLD:OFFICER'), allowedRoles are enum keys
  // (e.g. 'SALES_OFFICER') — convert each value to its key before comparing
  return userRoles.some((roleValue) => {
    const roleKey = roleValueToKey[roleValue]
    return roleKey !== undefined && allowedRoles.includes(roleKey)
  })
}

/**
 * Get all action definitions for a menu.
 * Useful for building permission matrices or UI.
 */
export function getMenuActionDefinitions(
  menu: string
): Record<string, ActionEntry> | undefined {
  return actionData[menu]?.actions
}

/**
 * Check if an action definition exists.
 */
export function hasActionDefinition(menu: string, action: string): boolean {
  return !!actionData[menu]?.actions[action]
}

// result is boolean → action found, use it
// result is null → action not found in JSON, fall through to roles fallback
export function hasMenuActionPermission(
  menu: string,
  action: string,
  auth: Auth | undefined
): boolean | null {
  const userRoles = auth?.user?.roleNames ?? []
  if (menu && action && userRoles.length > 0) {
    const result = canPerformMenuAction(menu, action, userRoles)
    if (result !== null) {
      // ADMIN and SUPER are handled inside canPerformMenuAction (SUPERADMIN)
      // but we also need to check ADMIN which hasRequiredRole handles
      return result || (auth?.hasRequiredRole([]) ?? false)
    }
  }
  return null
}
